import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Pencil, Trash2, FileText, ArrowLeft, Upload, X, CheckCircle, Save, UserPlus } from "lucide-react";
import "../pages/JobDetails.css";

/* ─────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────── */
const API_BASE = import.meta.env.VITE_API_URL || "https://thesalonx.in/api";

const ACTIVITY_TYPES = [
  "Office Briefing","Material Collection / Return","Travelling",
  "Scaffolding Erection / Dismantle","Before Photos","Alignment",
  "Site Survey","Surface Test","Article Cleaning","Electrical",
  "Plumbing","Carpentry","Sanding","Putty Application","Flattening",
  "Primer Application","Self Levelling Application","Old Removing",
  "Taking Measurement after Numbering","Material Cutting & Levelling",
  "Vinyl Wrapping","LVT / SPC Laying","Skirting Installation",
  "Quality Checking / Snagging","De-snagging / Rectification",
  "Silicone Application after Cleaning","Site Cleaning","After Photo",
  "Site Delivery","Break","Purchasing","Transporting",
  "Others (to be specified)",
];

const MATERIAL_OPTIONS = [
  "Vinyl Wrap Rolls","Paint","LVT / SPC Flooring","Carpet Roll",
  "Epoxy Resin","Micro-Cement","Stone Veneer","ACP Sheet",
  "Décor Panel","Adhesive / Primer","Masking Tape","Grout / Sealant",
];

const UNIT_OPTIONS = ["nos", "mtr", "box"];

const ACTIVITY_COLOR_MAP = {
  "Material Collection / Return": "green",
  "Travelling":                   "teal",
  "Surface Test":                 "blue",
  "Vinyl Wrapping":               "purple",
  "Office Briefing":              "orange",
  "Site Survey":                  "blue",
  "Article Cleaning":             "teal",
  "Site Cleaning":                "teal",
  "Break":                        "gray",
};
const getColor = (type) => ACTIVITY_COLOR_MAP[type] || "blue";

const STATUS_LIST   = ["pending","ongoing","hold","revisit","completed"];
const STATUS_LABELS = { pending:"Pending", ongoing:"Ongoing", hold:"Hold", revisit:"Revisit", completed:"Completed" };
const STATUS_CLS    = { pending:"s-pending", ongoing:"s-ongoing", hold:"s-hold", revisit:"s-revisit", completed:"s-completed" };

/* ─────────────────────────────────────────────
   AUTH HELPERS
───────────────────────────────────────────── */
const getLoggedUser = () => {
  try { return JSON.parse(localStorage.getItem("user")) || null; } catch { return null; }
};
const getToken = () =>
  localStorage.getItem("token") || sessionStorage.getItem("token") || "";

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const calcDuration = (s, e) => {
  if (!s || !e) return "—";
  const diff = (new Date(`1970-01-01T${e}`) - new Date(`1970-01-01T${s}`)) / 60000;
  if (diff <= 0) return "—";
  const h = Math.floor(diff / 60), m = diff % 60;
  return h === 0 ? `${m} min` : `${h}h ${String(m).padStart(2,"0")}m`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" });
};

const fmtShortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getMonth()+1).padStart(2,"0")}/${String(d.getDate()).padStart(2,"0")}/${String(d.getFullYear()).slice(2)}`;
};

const fmtTime = (t) => {
  if (!t) return "—";
  const [h, m] = t.split(":");
  const hour = parseInt(h, 10);
  return `${String(hour % 12 || 12).padStart(2,"0")}:${m} ${hour >= 12 ? "PM" : "AM"}`;
};

/* ─────────────────────────────────────────────
   CANVAS-BASED IMG → BASE64
───────────────────────────────────────────── */
const toBase64ViaCanvas = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth || 320; c.height = img.naturalHeight || 240;
        c.getContext("2d").drawImage(img, 0, 0);
        resolve(c.toDataURL("image/jpeg", 0.85));
      } catch { resolve(src); }
    };
    img.onerror = () => resolve(src);
    img.src = src.startsWith("data:") || src.startsWith("blob:")
      ? src
      : (src.includes("?") ? `${src}&_cb=${Date.now()}` : `${src}?_cb=${Date.now()}`);
  });

const b64ImgTag = async (src, alt = "") => {
  const data = await toBase64ViaCanvas(src);
  return `<img src="${data}" alt="${alt}" style="width:160px;height:120px;object-fit:cover;border-radius:6px;border:1px solid #e5e7eb;" />`;
};

const buildPhotoSection = async (label, urls) => {
  if (!urls || urls.length === 0)
    return `<h2 style="font-size:14px;font-weight:600;margin:20px 0 8px;color:#374151;border-bottom:1px solid #e5e7eb;padding-bottom:6px;">Photos — ${label}</h2><p style="font-size:12px;color:#9ca3af;margin-bottom:12px;">No ${label.toLowerCase()} photos.</p>`;
  const tags = await Promise.all(urls.map((u, i) => b64ImgTag(u, `${label} ${i+1}`)));
  return `
    <h2 style="font-size:14px;font-weight:600;margin:20px 0 8px;color:#374151;border-bottom:1px solid #e5e7eb;padding-bottom:6px;">Photos — ${label}</h2>
    <div style="display:flex;flex-wrap:wrap;gap:10px;margin-bottom:14px;">${tags.join("")}</div>
  `;
};

/* Role constants removed — role is not displayed in the edit panel */

/* ─────────────────────────────────────────────
   BASIC INFO EDIT CARD
   — admin / supervisor only
   — role is shown as a READ-ONLY badge, no editing
   — staff can be added or removed from the job
───────────────────────────────────────────── */
function BasicInfoEditCard({ job, allStaffPool, assignableUsers, onSave, onCancel }) {
  const [form, setForm] = useState({
    client_name: job.client_name || "",
    location:    job.location    || "",
    address:     job.address     || "",
    work_type:   job.work_type   || "",
  });

  /* assignedList: array of { id, name, email, phone, role } */
  const [assignedList, setAssignedList] = useState(
    assignableUsers.map(u => ({
      id:    String(u.id),
      name:  u.name,
      email: u.email || "",
      phone: u.phone || "",
      role:  (u.role || "staff").toLowerCase(),
    }))
  );

  const [dropOpen, setDropOpen] = useState(false);
  const [searchQ,  setSearchQ]  = useState("");
  const dropRef = useRef();

  const ch = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  /* Close dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropOpen(false);
        setSearchQ("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const assignedIds = assignedList.map(u => u.id);

  const addUser = (u) => {
    if (assignedIds.includes(String(u.id))) return;
    setAssignedList(prev => [...prev, {
      id:    String(u.id),
      name:  u.name,
      email: u.email || "",
      phone: u.phone || "",
      role:  (u.role || "staff").toLowerCase(),
    }]);
    setDropOpen(false);
    setSearchQ("");
  };

  const removeUser = (uid) =>
    setAssignedList(prev => prev.filter(u => u.id !== String(uid)));

  const filteredPool = allStaffPool.filter(u =>
    !assignedIds.includes(String(u.id)) &&
    (
      u.name.toLowerCase().includes(searchQ.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(searchQ.toLowerCase()) ||
      (u.phone || "").toLowerCase().includes(searchQ.toLowerCase())
    )
  );

  /* Only send staff_ids — roles are NOT changed here */
  const handleSave = () => {
    onSave({
      ...form,
      staff_ids: assignedList.map(u => u.id),
    });
  };

  return (
    <div className="jdf-edit-info-card">
      <div className="jdf-edit-info-title">
        <Pencil size={14} /> Edit Basic Information
      </div>

      <div className="jdf-edit-grid">
        <div className="jdf-field">
          <label className="jdf-label">Client Name</label>
          <input className="jdf-input" value={form.client_name} onChange={ch("client_name")} placeholder="Client name" />
        </div>
        <div className="jdf-field">
          <label className="jdf-label">Work Type</label>
          <input className="jdf-input" value={form.work_type} onChange={ch("work_type")} placeholder="Work type" />
        </div>
        <div className="jdf-field jdf-field-full">
          <label className="jdf-label">Location</label>
          <input className="jdf-input" value={form.location} onChange={ch("location")} placeholder="Location" />
        </div>
        <div className="jdf-field jdf-field-full">
          <label className="jdf-label">Address</label>
          <textarea className="jdf-input jdf-textarea" value={form.address} onChange={ch("address")} placeholder="Full address" rows={2} />
        </div>
      </div>

      {/* ── Staff assignment ── */}
      <div className="jdf-staff-assign-section">
        <div className="jdf-staff-assign-header">
          <span className="jdf-label">Assigned People</span>
          <span className="jdf-staff-count">{assignedList.length} assigned</span>
        </div>

        {/* Assigned list */}
        {assignedList.length === 0 && (
          <div className="jdf-assigned-empty">No one assigned yet. Use the button below to add.</div>
        )}
        {assignedList.length > 0 && (
          <div className="jdf-assigned-list">
            {assignedList.map((u) => (
              <div key={u.id} className="jdf-assigned-row">
                <div className="jdf-staff-pool-info">
                  <div className={`jdf-staff-avatar jdf-avatar-${u.role === "supervisor" ? "sup" : "staff"}`}>
                    {u.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="jdf-staff-pool-text">
                    <div className="jdf-staff-pool-name">{u.name}</div>
                    <div className="jdf-staff-pool-email">{u.email || u.phone || ""}</div>
                  </div>
                </div>

                <div className="jdf-assigned-controls">
                  {/* Remove button only — no role shown */}
                  <button
                    className="jdf-icon-btn jdf-icon-del"
                    title="Remove from job"
                    onClick={() => removeUser(u.id)}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add people dropdown */}
        <div className="jdf-add-person-wrap" ref={dropRef}>
          <button
            className="jdf-add-person-btn"
            onClick={() => { setDropOpen(o => !o); setSearchQ(""); }}
            type="button"
          >
            <UserPlus size={14} />
            Add Staff / Supervisor
            <span className="jdf-drop-arrow">{dropOpen ? "▲" : "▼"}</span>
          </button>

          {dropOpen && (
            <div className="jdf-add-person-dropdown">
              <div className="jdf-add-person-search-wrap">
                <input
                  className="jdf-input jdf-add-person-search"
                  placeholder="Search by name, email or phone…"
                  value={searchQ}
                  onChange={e => setSearchQ(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="jdf-add-person-list">
                {filteredPool.length === 0 && (
                  <div className="jdf-staff-pool-empty">
                    {allStaffPool.length === 0 ? "Loading users…" : "No more users to add"}
                  </div>
                )}
                {filteredPool.map(u => (
                  <div
                    key={u.id}
                    className="jdf-add-person-option"
                    onClick={() => addUser(u)}
                  >
                    <div className="jdf-staff-avatar" style={{ width:28, height:28, fontSize:11 }}>
                      {u.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="jdf-staff-pool-text">
                      <div className="jdf-staff-pool-name">{u.name}</div>
                      <div className="jdf-staff-pool-email">{u.email || u.phone || (u.role || "")}</div>
                    </div>
                    <UserPlus size={13} style={{ color: "#2563eb", flexShrink: 0 }} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="jdf-add-actions" style={{ marginTop: 4 }}>
        <button className="jdf-btn-outline" onClick={onCancel}>Cancel</button>
        <button className="jdf-btn-primary" onClick={handleSave}>
          <Save size={13} /> Save Info
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ADD / EDIT ACTIVITY CARD
───────────────────────────────────────────── */
function AddActivityCard({ onAdd, onCancel, editData, assignableUsers, isStaff, loggedUser }) {
  const [actType, setActType] = useState(editData?.activity_type || "");
  const [userId,  setUserId]  = useState(
    editData?.user_id
      ? String(editData.user_id)
      : isStaff && loggedUser?.id ? String(loggedUser.id) : ""
  );
  const [start, setStart] = useState(editData?.start_time || "");
  const [end,   setEnd]   = useState(editData?.end_time   || "");
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (!actType || !start || !end) { setError("Please fill in all fields."); return; }
    const diff = (new Date(`1970-01-01T${end}`) - new Date(`1970-01-01T${start}`)) / 60000;
    if (diff <= 0) { setError("End time must be after start time."); return; }
    setError("");
    onAdd({ activity_type: actType, user_id: userId, start_time: start, end_time: end });
  };

  return (
    <div className="jdf-add-card">
      <div className="jdf-add-title">{editData ? "Edit Activity" : "New Activity"}</div>

      <div className="jdf-field">
        <label className="jdf-label">Activity Type <span className="jdf-req">*</span></label>
        <select className="jdf-select" value={actType} onChange={e => setActType(e.target.value)}>
          <option value="">— Select Activity —</option>
          {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <div className="jdf-field">
        <label className="jdf-label">Performed By <span className="jdf-req">*</span></label>
        {isStaff ? (
          <div className="jdf-locked-user">
            <span>{loggedUser?.name || "You"}</span>
            <span className="jdf-you-badge">You</span>
          </div>
        ) : (
          <select className="jdf-select" value={userId} onChange={e => setUserId(e.target.value)} disabled={assignableUsers.length === 0}>
            <option value="">— Select staff member —</option>
            {assignableUsers.map(u => (
              <option key={u.id} value={String(u.id)}>{u.name}</option>
            ))}
          </select>
        )}
        {!isStaff && assignableUsers.length === 0 && (
          <span className="jdf-help" style={{ color:"#f59e0b" }}>No staff assigned to this job</span>
        )}
      </div>

      <div className="jdf-row-2">
        <div className="jdf-field">
          <label className="jdf-label">Start Time <span className="jdf-req">*</span></label>
          <input className="jdf-input" type="time" value={start} onChange={e => setStart(e.target.value)} />
        </div>
        <div className="jdf-field">
          <label className="jdf-label">End Time <span className="jdf-req">*</span></label>
          <input className="jdf-input" type="time" value={end} onChange={e => setEnd(e.target.value)} />
          <span className="jdf-help">End must be after start</span>
        </div>
      </div>

      {error && <div className="jdf-error">{error}</div>}

      <div className="jdf-add-actions">
        <button className="jdf-btn-outline" onClick={onCancel}>Cancel</button>
        <button className="jdf-btn-primary" onClick={handleSubmit}>
          {editData ? "Update Activity" : "Add Activity"}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ADD / EDIT MATERIAL CARD
───────────────────────────────────────────── */
function AddMaterialCard({ onAdd, onCancel, editData, assignableUsers, isStaff, loggedUser }) {
  const [matName, setMatName] = useState(editData?.material_name || MATERIAL_OPTIONS[0]);
  const [qty,     setQty]     = useState(editData?.quantity ?? 1);
  const [unit,    setUnit]    = useState(editData?.unit || UNIT_OPTIONS[0]);
  const [userId,  setUserId]  = useState(
    editData?.user_id
      ? String(editData.user_id)
      : isStaff && loggedUser?.id ? String(loggedUser.id) : ""
  );
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (!matName || !qty || qty < 1) { setError("Please fill all fields correctly."); return; }
    if (!unit) { setError("Please select a unit."); return; }
    setError("");
    onAdd({ material_name: matName, quantity: qty, unit, user_id: userId });
  };

  return (
    <div className="jdf-add-card">
      <div className="jdf-add-title">{editData ? "Edit Material" : "New Material"}</div>

      <div className="jdf-field">
        <label className="jdf-label">Material <span className="jdf-req">*</span></label>
        <select className="jdf-select" value={matName} onChange={e => setMatName(e.target.value)}>
          {MATERIAL_OPTIONS.map(o => <option key={o}>{o}</option>)}
        </select>
      </div>

      <div className="jdf-row-2">
        <div className="jdf-field">
          <label className="jdf-label">Quantity <span className="jdf-req">*</span></label>
          <input
            className="jdf-input"
            type="number"
            min={1}
            value={qty}
            onChange={e => setQty(e.target.value)}
          />
        </div>
        <div className="jdf-field">
          <label className="jdf-label">Unit <span className="jdf-req">*</span></label>
          <select className="jdf-select" value={unit} onChange={e => setUnit(e.target.value)}>
            {UNIT_OPTIONS.map(u => (
              <option key={u} value={u}>{u.toUpperCase()}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="jdf-field">
        <label className="jdf-label">Used By</label>
        {isStaff ? (
          <div className="jdf-locked-user">
            <span>{loggedUser?.name || "You"}</span>
            <span className="jdf-you-badge">You</span>
          </div>
        ) : (
          <select className="jdf-select" value={userId} onChange={e => setUserId(e.target.value)} disabled={assignableUsers.length === 0}>
            <option value="">— Select staff member —</option>
            {assignableUsers.map(u => (
              <option key={u.id} value={String(u.id)}>{u.name}</option>
            ))}
          </select>
        )}
      </div>

      {error && <div className="jdf-error">{error}</div>}

      <div className="jdf-add-actions">
        <button className="jdf-btn-outline" onClick={onCancel}>Cancel</button>
        <button className="jdf-btn-primary" onClick={handleSubmit}>
          {editData ? "Update Material" : "Add Material"}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   PHOTO SECTION
───────────────────────────────────────────── */
function PhotoSection({ label, existingUrls, newFiles, onNewFiles, disabled }) {
  const inputRef = useRef();

  const handleFiles = (e) => {
    if (disabled) return;
    const files = Array.from(e.target.files);
    const combined = [...newFiles, ...files].slice(0, 5 - existingUrls.length);
    onNewFiles(combined);
    e.target.value = "";
  };

  const removeNew = (i) => {
    if (disabled) return;
    onNewFiles(newFiles.filter((_, idx) => idx !== i));
  };

  return (
    <div className="jdf-photo-section">
      <div className="jdf-photo-section-label">{label}</div>
      <div className="jdf-photos-grid">
        {existingUrls.map((url, i) => (
          <div key={`ex-${i}`} className="jdf-photo-thumb">
            <img src={url} alt={`${label} ${i+1}`} />
            <span className="jdf-photo-tag">{label}</span>
          </div>
        ))}
        {newFiles.map((file, i) => (
          <div key={`new-${i}`} className="jdf-photo-thumb jdf-photo-new">
            <img src={URL.createObjectURL(file)} alt="new" />
            {!disabled && (
              <button className="jdf-photo-remove" onClick={() => removeNew(i)}><X size={10} /></button>
            )}
            <span className="jdf-photo-tag new">New</span>
          </div>
        ))}
        {!disabled && (existingUrls.length + newFiles.length) < 5 && (
          <div className="jdf-photo-upload-slot" onClick={() => inputRef.current?.click()}>
            <Upload size={16} />
            <span>Upload</span>
            <input ref={inputRef} type="file" accept="image/*" multiple
              style={{ display:"none" }} onChange={handleFiles} />
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
function JobDetailsForm() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const token       = getToken();
  const loggedUser  = getLoggedUser();
  const loggedRole  = loggedUser?.role?.toLowerCase() || "";
  const isStaff     = loggedRole === "staff";
  const isPrivileged = ["admin", "supervisor"].includes(loggedRole);

  const authHeaders = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const [job,       setJob]       = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [photos,    setPhotos]    = useState({ before:[], during:[], after:[] });
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");

  const isCompleted      = job?.status === "completed";
  const isStaffCompleted = isStaff && isCompleted;

  const [allActivities, setAllActivities] = useState([]);
  const [allMaterials,  setAllMaterials]  = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [allStaffPool, setAllStaffPool] = useState([]);

  const [selectedStatus,    setSelectedStatus]    = useState("");
  const [editingBasicInfo,  setEditingBasicInfo]  = useState(false);

  const [showAddActivity,  setShowAddActivity]  = useState(false);
  const [editActivityIdx,  setEditActivityIdx]  = useState(null);
  const [newActivities,    setNewActivities]    = useState([]);

  const [showAddMaterial,  setShowAddMaterial]  = useState(false);
  const [editMaterialIdx,  setEditMaterialIdx]  = useState(null);
  const [newMaterials,     setNewMaterials]     = useState([]);

  const [editingActivity,  setEditingActivity]  = useState(null);
  const [editingMaterial,  setEditingMaterial]  = useState(null);

  const [photosBefore, setPhotosBefore] = useState([]);
  const [photosDuring, setPhotosDuring] = useState([]);
  const [photosAfter,  setPhotosAfter]  = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [submitMsg,  setSubmitMsg]  = useState("");
  const [submitErr,  setSubmitErr]  = useState("");

  /* ── Fetch job ── */
  const fetchJob = () => {
    setLoading(true);
    fetch(`${API_BASE}/job/${id}`, { headers: authHeaders })
      .then(r => r.json())
      .then(d => {
        if (d.status || d.success) {
          const data = d.data;
          setJob(data.job);
          setSelectedStatus(data.job.status);

          const ud = data.users_data ?? [];
          setUsersData(ud);

          const acts = [], mats = [];
          ud.forEach(({ user, activities, materials }) => {
            (activities ?? []).forEach(a => acts.push({ ...a, _userName: user?.name }));
            (materials  ?? []).forEach(m => mats.push({ ...m, _userName: user?.name }));
          });
          setAllActivities(acts);
          setAllMaterials(mats);

          setAssignableUsers(ud.map(u => u.user).filter(Boolean));
          setPhotos(data.photos ?? { before:[], during:[], after:[] });
        } else {
          setError(d.message || "Failed to load job.");
        }
      })
      .catch(() => setError("Network error. Please try again."))
      .finally(() => setLoading(false));
  };

  /* ── Fetch all users (admin/supervisor) ── */
  const fetchAllStaff = () => {
    if (!isPrivileged) return;
    fetch(`${API_BASE}/users/staff-supervisors`, { headers: authHeaders })
      .then(r => r.json())
      .then(d => {
        const list = d.data || d.users || d || [];
        if (Array.isArray(list) && list.length > 0) {
          setAllStaffPool(list);
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (id) { fetchJob(); fetchAllStaff(); }
  }, [id]);

  /* ── Activity queue handlers ── */
  const handleAddActivity = (entry) => {
    if (editActivityIdx !== null) {
      const upd = [...newActivities]; upd[editActivityIdx] = entry;
      setNewActivities(upd); setEditActivityIdx(null);
    } else {
      setNewActivities(p => [...p, entry]);
    }
    setShowAddActivity(false);
  };

  const handleEditQueuedActivity  = (i) => { setEditActivityIdx(i); setShowAddActivity(true); };
  const handleDeleteQueuedActivity = (i) => {
    setNewActivities(p => p.filter((_, idx) => idx !== i));
    if (editActivityIdx === i) { setShowAddActivity(false); setEditActivityIdx(null); }
  };

  /* ── Material queue handlers ── */
  const handleAddMaterial = (entry) => {
    if (editMaterialIdx !== null) {
      const upd = [...newMaterials]; upd[editMaterialIdx] = entry;
      setNewMaterials(upd); setEditMaterialIdx(null);
    } else {
      setNewMaterials(p => [...p, entry]);
    }
    setShowAddMaterial(false);
  };

  const handleEditQueuedMaterial  = (i) => { setEditMaterialIdx(i); setShowAddMaterial(true); };
  const handleDeleteQueuedMaterial = (i) => {
    setNewMaterials(p => p.filter((_, idx) => idx !== i));
    if (editMaterialIdx === i) { setShowAddMaterial(false); setEditMaterialIdx(null); }
  };

  /* ── Username lookup ── */
  const getUserName = (uid) => {
    if (isStaff && String(loggedUser?.id) === String(uid)) return loggedUser?.name || "You";
    const u = assignableUsers.find(u => String(u.id) === String(uid));
    return u?.name || `Staff #${uid}`;
  };

  /* ── Save basic info — no staff_roles sent ── */
  const handleSaveBasicInfo = ({ client_name, location, address, work_type, staff_ids }) => {
    const fd = new FormData();
    fd.append("status",      selectedStatus);
    fd.append("client_name", client_name);
    fd.append("location",    location);
    fd.append("address",     address);
    fd.append("work_type",   work_type);
    staff_ids.forEach(sid => fd.append("staff_id[]", sid));

    fetch(`${API_BASE}/job/update/${id}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    })
      .then(r => r.json())
      .then(d => {
        if (d.status || d.success) {
          setEditingBasicInfo(false);
          setSubmitMsg("Basic info updated!");
          fetchJob();
          setTimeout(() => setSubmitMsg(""), 3000);
        } else {
          setSubmitErr(d.message || "Update failed.");
        }
      })
      .catch(() => setSubmitErr("Network error."));
  };

  /* ── Main submit ── */
  const handleSubmit = async () => {
    if (isStaffCompleted) return;

    setSubmitting(true); setSubmitMsg(""); setSubmitErr("");

    const fd = new FormData();
    fd.append("status", selectedStatus);

    const actsToSend = isStaff
      ? [...allActivities.filter(a => String(a.user_id) === String(loggedUser?.id)), ...newActivities]
      : [...allActivities, ...newActivities];

    actsToSend.forEach((a, i) => {
      fd.append(`activity_type[${i}]`,    a.activity_type);
      fd.append(`activity_user_id[${i}]`, a.user_id || a._userId || (isStaff ? loggedUser?.id : ""));
      if (a.start_time) fd.append(`start_time[${i}]`, a.start_time);
      if (a.end_time)   fd.append(`end_time[${i}]`,   a.end_time);
    });

    const matsToSend = isStaff
      ? [...allMaterials.filter(m => String(m.user_id) === String(loggedUser?.id)), ...newMaterials]
      : [...allMaterials, ...newMaterials];

    matsToSend.forEach((m, i) => {
      fd.append(`material_name[${i}]`,    m.material_name);
      fd.append(`material_user_id[${i}]`, m.user_id || (isStaff ? loggedUser?.id : ""));
      fd.append(`quantity[${i}]`,         m.quantity);
      fd.append(`unit[${i}]`,             m.unit || "nos");
    });

    photosBefore.forEach(f => fd.append("photos_before[]", f));
    photosDuring.forEach(f => fd.append("photos_during[]", f));
    photosAfter.forEach(f  => fd.append("photos_after[]",  f));

    try {
      const res = await fetch(`${API_BASE}/job/update/${id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const d = await res.json();
      if (d.status || d.success) {
        setSubmitMsg("Job updated successfully!");
        setNewActivities([]); setNewMaterials([]);
        setPhotosBefore([]); setPhotosDuring([]); setPhotosAfter([]);
        fetchJob();
      } else {
        setSubmitErr(d.message || "Update failed.");
      }
    } catch {
      setSubmitErr("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── PDF Export ── */
  const handleExportPDF = async () => {
    if (!job) return;

    const exportActivities = isStaff
      ? allActivities.filter(a => String(a.user_id) === String(loggedUser?.id))
      : allActivities;

    const exportMaterials = isStaff
      ? allMaterials.filter(m => String(m.user_id) === String(loggedUser?.id))
      : allMaterials;

    const beforeUrls = [...(photos.before ?? []), ...photosBefore.map(f => URL.createObjectURL(f))];
    const duringUrls = [...(photos.during ?? []), ...photosDuring.map(f => URL.createObjectURL(f))];
    const afterUrls  = [...(photos.after  ?? []), ...photosAfter.map(f  => URL.createObjectURL(f))];

    const [beforeHtml, duringHtml, afterHtml] = await Promise.all([
      buildPhotoSection("Before", beforeUrls),
      buildPhotoSection("During", duringUrls),
      buildPhotoSection("After",  afterUrls),
    ]);

    const staffLabel = isStaff ? ` — ${loggedUser?.name || "Staff"}` : "";

    let materialsHtml = "";
    if (exportMaterials.length > 0) {
      materialsHtml = `
        <h2 style="font-size:14px;font-weight:600;margin:20px 0 8px;color:#374151;border-bottom:1px solid #e5e7eb;padding-bottom:6px;">Materials Used${staffLabel}</h2>
        <table style="width:100%;border-collapse:collapse;margin-bottom:14px;">
          <thead><tr>
            <th style="background:#f9fafb;padding:8px 10px;text-align:left;font-size:10.5px;font-weight:600;letter-spacing:.05em;color:#6b7280;border-bottom:1px solid #e5e7eb;">MATERIAL</th>
            ${!isStaff ? '<th style="background:#f9fafb;padding:8px 10px;text-align:left;font-size:10.5px;font-weight:600;color:#6b7280;border-bottom:1px solid #e5e7eb;">STAFF</th>' : ""}
            <th style="background:#f9fafb;padding:8px 10px;text-align:left;font-size:10.5px;font-weight:600;color:#6b7280;border-bottom:1px solid #e5e7eb;">QTY</th>
            <th style="background:#f9fafb;padding:8px 10px;text-align:left;font-size:10.5px;font-weight:600;color:#6b7280;border-bottom:1px solid #e5e7eb;">UNIT</th>
          </tr></thead>
          <tbody>
            ${exportMaterials.map(m => `<tr>
              <td style="padding:9px 10px;font-size:12.5px;color:#374151;border-bottom:1px solid #f3f4f6;">${m.material_name}</td>
              ${!isStaff ? `<td style="padding:9px 10px;font-size:12.5px;color:#374151;border-bottom:1px solid #f3f4f6;">${m._userName || getUserName(m.user_id)}</td>` : ""}
              <td style="padding:9px 10px;font-size:12.5px;color:#374151;border-bottom:1px solid #f3f4f6;">${m.quantity}</td>
              <td style="padding:9px 10px;font-size:12.5px;color:#374151;border-bottom:1px solid #f3f4f6;text-transform:uppercase;">${m.unit || "nos"}</td>
            </tr>`).join("")}
          </tbody>
        </table>
      `;
    }

    let activitiesHtml = "";
    if (exportActivities.length > 0) {
      activitiesHtml = `
        <h2 style="font-size:14px;font-weight:600;margin:20px 0 8px;color:#374151;border-bottom:1px solid #e5e7eb;padding-bottom:6px;">Activity Logs${staffLabel}</h2>
        <table style="width:100%;border-collapse:collapse;margin-bottom:14px;">
          <thead><tr>
            <th style="background:#f9fafb;padding:8px 10px;text-align:left;font-size:10.5px;font-weight:600;color:#6b7280;border-bottom:1px solid #e5e7eb;">ACTIVITY</th>
            ${!isStaff ? '<th style="background:#f9fafb;padding:8px 10px;text-align:left;font-size:10.5px;font-weight:600;color:#6b7280;border-bottom:1px solid #e5e7eb;">STAFF</th>' : ""}
            <th style="background:#f9fafb;padding:8px 10px;text-align:left;font-size:10.5px;font-weight:600;color:#6b7280;border-bottom:1px solid #e5e7eb;">START</th>
            <th style="background:#f9fafb;padding:8px 10px;text-align:left;font-size:10.5px;font-weight:600;color:#6b7280;border-bottom:1px solid #e5e7eb;">END</th>
            <th style="background:#f9fafb;padding:8px 10px;text-align:left;font-size:10.5px;font-weight:600;color:#6b7280;border-bottom:1px solid #e5e7eb;">DURATION</th>
          </tr></thead>
          <tbody>
            ${exportActivities.map(a => `<tr>
              <td style="padding:9px 10px;font-size:12.5px;color:#374151;border-bottom:1px solid #f3f4f6;">${a.activity_type}</td>
              ${!isStaff ? `<td style="padding:9px 10px;font-size:12.5px;color:#374151;border-bottom:1px solid #f3f4f6;">${a._userName || getUserName(a.user_id)}</td>` : ""}
              <td style="padding:9px 10px;font-size:12.5px;color:#374151;border-bottom:1px solid #f3f4f6;">${fmtTime(a.start_time)}</td>
              <td style="padding:9px 10px;font-size:12.5px;color:#374151;border-bottom:1px solid #f3f4f6;">${fmtTime(a.end_time)}</td>
              <td style="padding:9px 10px;font-size:12.5px;color:#374151;border-bottom:1px solid #f3f4f6;">${calcDuration(a.start_time, a.end_time)}</td>
            </tr>`).join("")}
          </tbody>
        </table>
      `;
    }

    const staffListHtml = !isStaff && assignableUsers.length > 0 ? `
      <span style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.04em;">Staff</span>
      <span style="font-size:13px;color:#111;">${assignableUsers.map(u => u.name).join(", ")}</span>
    ` : (isStaff ? `
      <span style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.04em;">Staff</span>
      <span style="font-size:13px;color:#111;">${loggedUser?.name || "—"}</span>
    ` : "");

    const bodyHtml = `
      <h1 style="font-size:20px;font-weight:700;margin-bottom:4px;">Job #${job.id} — ${STATUS_LABELS[job.status] ?? job.status}</h1>
      <p style="font-size:12px;color:#6b7280;margin-bottom:24px;">Generated on ${new Date().toLocaleString("en-GB")}${isStaff ? ` · ${loggedUser?.name}` : ""}</p>
      <h2 style="font-size:14px;font-weight:600;margin:20px 0 8px;color:#374151;border-bottom:1px solid #e5e7eb;padding-bottom:6px;">Basic Information</h2>
      <div style="display:grid;grid-template-columns:140px 1fr;gap:8px 12px;margin-bottom:16px;">
        <span style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.04em;">Client</span>
        <span style="font-size:13px;color:#111;">${job.client_name ?? "—"}</span>
        <span style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.04em;">Location</span>
        <span style="font-size:13px;color:#111;">${job.location ?? "—"}</span>
        <span style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.04em;">Address</span>
        <span style="font-size:13px;color:#111;">${job.address ?? "—"}</span>
        <span style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.04em;">Work Type</span>
        <span style="font-size:13px;color:#111;">${job.work_type ?? "—"}</span>
        ${staffListHtml}
        <span style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.04em;">Status</span>
        <span style="font-size:13px;color:#111;">${STATUS_LABELS[job.status] ?? job.status}</span>
        <span style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.04em;">Created</span>
        <span style="font-size:13px;color:#111;">${fmtDate(job.created_at)}</span>
      </div>
      ${materialsHtml}
      ${activitiesHtml}
      ${beforeHtml}
      ${duringHtml}
      ${afterHtml}
    `;

    const win = window.open("", "_blank", "width=960,height=720");
    if (!win) { alert("Pop-up blocked. Please allow pop-ups and try again."); return; }
    win.document.write(`
      <html><head><title>Job #${job.id} Report</title>
      <style>*{box-sizing:border-box;margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}body{padding:32px;background:#fff;color:#111;font-size:13px;}</style>
      </head><body>${bodyHtml}</body></html>
    `);
    win.document.close();
    win.onload = () => { win.focus(); win.print(); };
  };

  /* ── Loading / Error ── */
  if (loading) return (
    <div className="jdf-state-wrap">
      <div className="jdf-spinner" />
      <p>Loading job details…</p>
    </div>
  );
  if (error) return (
    <div className="jdf-state-wrap jdf-error-state">
      <p>{error}</p>
      <button className="jdf-btn-outline" onClick={() => navigate(-1)}>← Go Back</button>
    </div>
  );
  if (!job) return null;

  const combinedActivities = [...allActivities, ...newActivities];
  const combinedMaterials  = [...allMaterials,  ...newMaterials];

  /* Merge API pool + already-assigned so chips always resolve */
  const mergedStaffPool = (() => {
    const base = [...allStaffPool];
    assignableUsers.forEach(u => {
      if (!base.find(x => String(x.id) === String(u.id))) base.push(u);
    });
    return base;
  })();

  const getTabCls = (s) => {
    if (s !== selectedStatus) return "";
    if (s === "pending")  return "jdf-tab-pending";
    if (s === "ongoing")  return "jdf-tab-ongoing-active";
    return "jdf-tab-active";
  };

  const staffDisplay = assignableUsers.length > 0
    ? assignableUsers.map(u => u.name).join(", ")
    : (job.staff_name || "—");

  /* ═══════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════ */
  return (
    <div className="jdf-wrap">

      {/* Title row */}
      <div className="jdf-title-row">
        <div>
          <h2 className="jdf-page-title">Job #{job.id}</h2>
          <div className="jdf-breadcrumb">
            <span className="jdf-bc-link" onClick={() => navigate("/dashboard")}>Dashboard</span>
            <span className="jdf-bc-sep">›</span>
            <span className="jdf-bc-current">Job #{job.id}</span>
          </div>
        </div>
        <div className="jdf-title-actions">
          <button className="jdf-btn-outline jdf-hdr-btn" onClick={handleExportPDF}>
            <FileText size={14} /> Export PDF
          </button>
          <button className="jdf-btn-outline jdf-hdr-btn" onClick={() => navigate(-1)}>
            <ArrowLeft size={14} /> Back
          </button>
        </div>
      </div>

      {/* Role info chip */}
      {isStaff && !isStaffCompleted && (
        <div className="jdf-role-hint">
          Viewing your activities &amp; materials only. Photos are shared across all staff.
        </div>
      )}

      {/* Staff completed banner */}
      {isStaffCompleted && (
        <div className="jdf-role-hint jdf-role-hint--danger">
          🔒 This job is completed. You cannot modify activities, materials, photos or status.
        </div>
      )}

      {/* STATUS TAB BAR */}
      <div className="jdf-tabs">
        {STATUS_LIST.map((s) => (
          <button
            key={s}
            className={`jdf-tab ${getTabCls(s)}`}
            onClick={() => { if (!isStaffCompleted) setSelectedStatus(s); }}
            disabled={isStaffCompleted}
            title={isStaffCompleted ? "Job is completed — cannot change status" : undefined}
          >
            {STATUS_LABELS[s]}
            {s === "ongoing" && selectedStatus === "ongoing" && <span className="jdf-tab-dot" />}
          </button>
        ))}
      </div>

      {/* TWO-COLUMN CONTENT */}
      <div className="jdf-content">

        {/* LEFT */}
        <div className="jdf-left">

          {/* BASIC INFORMATION */}
          <div className="jdf-card">
            <div className="jdf-card-header">
              <h3 className="jdf-card-title">Basic Information</h3>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {isPrivileged && !editingBasicInfo && (
                  <button className="jdf-btn-outline jdf-hdr-btn"
                    onClick={() => setEditingBasicInfo(true)}>
                    <Pencil size={13} /> Edit Info
                  </button>
                )}
                {!isStaffCompleted && (
                  <button className="jdf-change-status-btn"
                    onClick={() => window.scrollTo({ top:0, behavior:"smooth" })}>
                    Change Status
                  </button>
                )}
              </div>
            </div>

            {editingBasicInfo ? (
              <BasicInfoEditCard
                job={job}
                allStaffPool={mergedStaffPool}
                assignableUsers={assignableUsers}
                onSave={handleSaveBasicInfo}
                onCancel={() => setEditingBasicInfo(false)}
              />
            ) : (
              <div className="jdf-info-table">
                {[
                  { label:"CLIENT",    value: job.client_name, bold: true },
                  { label:"LOCATION",  value: job.location },
                  { label:"ADDRESS",   value: job.address },
                  { label:"STAFF",     value: staffDisplay },
                  { label:"WORK TYPE", value: job.work_type },
                  { label:"STATUS",    value: null, statusNode: true },
                  { label:"CREATED",   value: fmtDate(job.created_at) },
                ].map((row) => (
                  <div className="jdf-info-row" key={row.label}>
                    <span className="jdf-info-label">{row.label}</span>
                    {row.statusNode ? (
                      <span className="jdf-info-value">
                        <span className={`jdf-badge-status ${STATUS_CLS[job.status] ?? ""}`}>
                          {STATUS_LABELS[job.status] ?? job.status}
                        </span>
                        {!isStaffCompleted && (
                          <span className="jdf-status-note">Both admin &amp; staff can change</span>
                        )}
                      </span>
                    ) : (
                      <span className={`jdf-info-value${row.bold ? " bold" : ""}`}>
                        {row.value || "—"}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* MATERIALS */}
          <div className="jdf-card">
            <div className="jdf-card-header">
              <div>
                <h3 className="jdf-card-title">Materials Used</h3>
                {isStaff && (
                  <p style={{ fontSize:12, color:"#9ca3af", marginTop:2 }}>Your materials only</p>
                )}
              </div>
              {!showAddMaterial && !isStaffCompleted && (
                <button className="jdf-btn-primary"
                  onClick={() => { setShowAddMaterial(true); setEditMaterialIdx(null); }}>
                  + Add Material
                </button>
              )}
            </div>

            {allMaterials.length > 0 && (
              <div className="jdf-table-wrap">
                <table className="jdf-mat-table-full">
                  <thead>
                    <tr>
                      <th className="jdf-th">Material</th>
                      {isPrivileged && <th className="jdf-th">Staff</th>}
                      <th className="jdf-th jdf-th-center">Qty</th>
                      <th className="jdf-th jdf-th-center">Unit</th>
                      <th className="jdf-th jdf-th-right">Logged</th>
                      {isPrivileged && <th className="jdf-th" style={{ width:64 }}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {allMaterials.map((m, i) => (
                      <tr key={i} className="jdf-tr">
                        <td className="jdf-td jdf-td-main">{m.material_name}</td>
                        {isPrivileged && (
                          <td className="jdf-td jdf-td-staff">{m._userName || getUserName(m.user_id)}</td>
                        )}
                        <td className="jdf-td jdf-td-center">
                          <span className="jdf-qty-badge">{m.quantity}</span>
                        </td>
                        <td className="jdf-td jdf-td-center">
                          <span className="jdf-unit-badge">{(m.unit || "nos").toUpperCase()}</span>
                        </td>
                        <td className="jdf-td jdf-td-right">
                          <span className="jdf-mat-logged">{fmtShortDate(m.created_at || job.created_at)}</span>
                        </td>
                        {isPrivileged && (
                          <td className="jdf-td">
                            <div className="jdf-act-btns">
                              <button className="jdf-icon-btn" title="Edit"
                                onClick={() => { setEditingMaterial({ idx: i, data: m }); setShowAddMaterial(false); setEditMaterialIdx(null); }}>
                                <Pencil size={12} />
                              </button>
                              <button className="jdf-icon-btn jdf-icon-del" title="Delete"
                                onClick={() => setAllMaterials(p => p.filter((_, idx) => idx !== i))}>
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {allMaterials.length === 0 && !showAddMaterial && (
              <p style={{ color:"#9ca3af", fontSize:13, padding:"8px 0" }}>No materials logged yet.</p>
            )}

            {newMaterials.length > 0 && (
              <>
                <div className="jdf-new-act-label">New (not yet saved)</div>
                <div className="jdf-table-wrap">
                  <table className="jdf-mat-table-full">
                    <thead>
                      <tr>
                        <th className="jdf-th">Material</th>
                        {isPrivileged && <th className="jdf-th">Staff</th>}
                        <th className="jdf-th jdf-th-center">Qty</th>
                        <th className="jdf-th jdf-th-center">Unit</th>
                        <th className="jdf-th jdf-th-right">Logged</th>
                        <th className="jdf-th" style={{ width:64 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {newMaterials.map((m, i) => (
                        <tr key={i} className="jdf-tr jdf-row-new">
                          <td className="jdf-td jdf-td-main">{m.material_name}</td>
                          {isPrivileged && (
                            <td className="jdf-td jdf-td-staff">{getUserName(m.user_id)}</td>
                          )}
                          <td className="jdf-td jdf-td-center">
                            <span className="jdf-qty-badge">{m.quantity}</span>
                          </td>
                          <td className="jdf-td jdf-td-center">
                            <span className="jdf-unit-badge">{(m.unit || "nos").toUpperCase()}</span>
                          </td>
                          <td className="jdf-td jdf-td-right">
                            <span className="jdf-mat-logged">Today</span>
                          </td>
                          <td className="jdf-td">
                            <div className="jdf-act-btns">
                              <button className="jdf-icon-btn" onClick={() => handleEditQueuedMaterial(i)}><Pencil size={12} /></button>
                              <button className="jdf-icon-btn jdf-icon-del" onClick={() => handleDeleteQueuedMaterial(i)}><Trash2 size={12} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {editingMaterial && (
              <AddMaterialCard
                editData={editingMaterial.data}
                assignableUsers={assignableUsers}
                isStaff={isStaff}
                loggedUser={loggedUser}
                onAdd={(entry) => {
                  const upd = [...allMaterials];
                  upd[editingMaterial.idx] = { ...upd[editingMaterial.idx], ...entry };
                  setAllMaterials(upd);
                  setEditingMaterial(null);
                }}
                onCancel={() => setEditingMaterial(null)}
              />
            )}

            {showAddMaterial && !isStaffCompleted && (
              <AddMaterialCard
                editData={editMaterialIdx !== null ? newMaterials[editMaterialIdx] : null}
                assignableUsers={assignableUsers}
                isStaff={isStaff}
                loggedUser={loggedUser}
                onAdd={handleAddMaterial}
                onCancel={() => { setShowAddMaterial(false); setEditMaterialIdx(null); }}
              />
            )}
          </div>

          {/* PHOTOS */}
          <div className="jdf-card">
            <div className="jdf-card-header">
              <h3 className="jdf-card-title">Photos</h3>
              <span style={{ fontSize:11, color:"#9ca3af", fontStyle:"italic" }}>Shared across all staff</span>
            </div>
            {isStaffCompleted && (
              <p style={{ fontSize:12, color:"#9ca3af", marginBottom:10 }}>View only — job is completed.</p>
            )}
            <div style={{ marginTop:14 }}>
              <PhotoSection label="Before" existingUrls={photos.before ?? []} newFiles={photosBefore} onNewFiles={setPhotosBefore} disabled={isStaffCompleted} />
              <PhotoSection label="During" existingUrls={photos.during ?? []} newFiles={photosDuring} onNewFiles={setPhotosDuring} disabled={isStaffCompleted} />
              <PhotoSection label="After"  existingUrls={photos.after  ?? []} newFiles={photosAfter}  onNewFiles={setPhotosAfter}  disabled={isStaffCompleted} />
            </div>
          </div>

        </div>

        {/* RIGHT — Activity Logs */}
        <div className="jdf-right">
          <div className="jdf-card">
            <div className="jdf-card-header">
              <div>
                <h3 className="jdf-card-title">Activity Logs</h3>
                <p className="jdf-act-sub">
                  {isStaff ? "Your activities only" : "All staff · Added when Ongoing or Revisit"}
                </p>
              </div>
              {!showAddActivity && !isStaffCompleted && (
                <button className="jdf-btn-primary"
                  onClick={() => { setShowAddActivity(true); setEditActivityIdx(null); }}>
                  + Add Activity
                </button>
              )}
            </div>

            {allActivities.length > 0 && (
              <div className="jdf-table-wrap">
                <table className="jdf-act-table">
                  <thead>
                    <tr>
                      <th className="jdf-th" style={{ width:72 }}>Date</th>
                      {isPrivileged && <th className="jdf-th" style={{ width:88 }}>Staff</th>}
                      <th className="jdf-th">Activity</th>
                      <th className="jdf-th" style={{ width:62 }}>Start</th>
                      <th className="jdf-th" style={{ width:62 }}>End</th>
                      <th className="jdf-th" style={{ width:72 }}>Duration</th>
                      {isPrivileged && <th className="jdf-th" style={{ width:60 }}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {allActivities.map((log, i) => (
                      <tr key={i} className="jdf-tr">
                        <td className="jdf-td jdf-td-date">{fmtShortDate(log.created_at || job.created_at)}</td>
                        {isPrivileged && (
                          <td className="jdf-td jdf-td-staff">{log._userName || getUserName(log.user_id)}</td>
                        )}
                        <td className="jdf-td">
                          <span className={`jdf-act-badge jdf-act-${getColor(log.activity_type)}`}>
                            {log.activity_type}
                          </span>
                        </td>
                        <td className="jdf-td jdf-td-time">{fmtTime(log.start_time)}</td>
                        <td className="jdf-td jdf-td-time">{fmtTime(log.end_time)}</td>
                        <td className="jdf-td jdf-td-dur">{calcDuration(log.start_time, log.end_time)}</td>
                        {isPrivileged && (
                          <td className="jdf-td">
                            <div className="jdf-act-btns">
                              <button className="jdf-icon-btn" title="Edit"
                                onClick={() => { setEditingActivity({ idx: i, data: log }); setShowAddActivity(false); setEditActivityIdx(null); }}>
                                <Pencil size={12} />
                              </button>
                              <button className="jdf-icon-btn jdf-icon-del" title="Delete"
                                onClick={() => setAllActivities(p => p.filter((_, idx) => idx !== i))}>
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {newActivities.length > 0 && (
              <>
                <div className="jdf-new-act-label">New (not yet saved)</div>
                <div className="jdf-table-wrap">
                  <table className="jdf-act-table">
                    <thead>
                      <tr>
                        <th className="jdf-th" style={{ width:72 }}>Date</th>
                        {isPrivileged && <th className="jdf-th" style={{ width:88 }}>Staff</th>}
                        <th className="jdf-th">Activity</th>
                        <th className="jdf-th" style={{ width:62 }}>Start</th>
                        <th className="jdf-th" style={{ width:62 }}>End</th>
                        <th className="jdf-th" style={{ width:72 }}>Duration</th>
                        <th className="jdf-th" style={{ width:60 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {newActivities.map((log, i) => (
                        <tr key={i} className="jdf-tr jdf-row-new">
                          <td className="jdf-td jdf-td-date">{fmtShortDate(new Date().toISOString())}</td>
                          {isPrivileged && (
                            <td className="jdf-td jdf-td-staff">{getUserName(log.user_id)}</td>
                          )}
                          <td className="jdf-td">
                            <span className={`jdf-act-badge jdf-act-${getColor(log.activity_type)}`}>
                              {log.activity_type}
                            </span>
                          </td>
                          <td className="jdf-td jdf-td-time">{fmtTime(log.start_time)}</td>
                          <td className="jdf-td jdf-td-time">{fmtTime(log.end_time)}</td>
                          <td className="jdf-td jdf-td-dur">{calcDuration(log.start_time, log.end_time)}</td>
                          <td className="jdf-td">
                            <div className="jdf-act-btns">
                              <button className="jdf-icon-btn" onClick={() => handleEditQueuedActivity(i)}><Pencil size={12} /></button>
                              <button className="jdf-icon-btn jdf-icon-del" onClick={() => handleDeleteQueuedActivity(i)}><Trash2 size={12} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {allActivities.length === 0 && newActivities.length === 0 && !showAddActivity && (
              <div className="jdf-act-empty">No activities logged yet.</div>
            )}

            {editingActivity && (
              <AddActivityCard
                editData={editingActivity.data}
                assignableUsers={assignableUsers}
                isStaff={isStaff}
                loggedUser={loggedUser}
                onAdd={(entry) => {
                  const upd = [...allActivities];
                  upd[editingActivity.idx] = { ...upd[editingActivity.idx], ...entry };
                  setAllActivities(upd);
                  setEditingActivity(null);
                }}
                onCancel={() => setEditingActivity(null)}
              />
            )}

            {showAddActivity && !isStaffCompleted && (
              <AddActivityCard
                editData={editActivityIdx !== null ? newActivities[editActivityIdx] : null}
                assignableUsers={assignableUsers}
                isStaff={isStaff}
                loggedUser={loggedUser}
                onAdd={handleAddActivity}
                onCancel={() => { setShowAddActivity(false); setEditActivityIdx(null); }}
              />
            )}

            <div className="jdf-act-footer">
              {combinedActivities.length} {combinedActivities.length === 1 ? "activity" : "activities"}
              {isPrivileged && " · Activities cannot overlap for same staff"}
            </div>
          </div>
        </div>

      </div>

      {/* BOTTOM BAR */}
      <div className="jdf-bottom-bar">
        <button className="jdf-btn-outline" onClick={() => navigate(-1)}>
          <ArrowLeft size={14} /> Back
        </button>
        <div className="jdf-bottom-right">
          {submitMsg && (
            <div className="jdf-submit-success">
              <CheckCircle size={15} /> {submitMsg}
            </div>
          )}
          {submitErr && <div className="jdf-submit-error">{submitErr}</div>}
          <button className="jdf-btn-outline jdf-hdr-btn" onClick={handleExportPDF}>
            <FileText size={14} /> Export PDF
          </button>
          {!isStaffCompleted && (
            <button className="jdf-btn-primary jdf-submit-btn" onClick={handleSubmit} disabled={submitting}>
              {submitting
                ? <><span className="jdf-btn-spinner" /> Saving…</>
                : <><span>✓</span> Save Changes</>
              }
            </button>
          )}
        </div>
      </div>

    </div>
  );
}

export default JobDetailsForm;